import React, { useState } from "react";
import { AccordionContentStyled } from "../../../styled/Accordion";
import { ImageAltStyled } from "../../../styled/JobConnection";
import { useTheme } from "../Theme/ThemeContext";
import { FaLinkedin, FaRegCopy, FaCheck } from "react-icons/fa";
import MainModal from "../Modal/MainModal";
import styled from "styled-components";
import LoadingData from "../Loading/LoadingData";
import { Vortex } from "react-loader-spinner";
import axios from "axios";

const DataContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    padding: 1rem;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;

    @media (max-width: 768px) {
        margin: 0.5rem 0;
        padding: 0.5rem;
    }

    h1 {
        font-size: 1.4rem;
        background: linear-gradient(90deg, #0069b9, #EE7B1C);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        font-weight: 800;
        text-align: center;
        margin-bottom: 0.5rem;
    }

    .message-box {
        position: relative;
        width: 100%;
        background: ${props => props.theme === 'dark' ? '#1a1a1a' : '#f9f9f9'};
        border: 1px solid ${props => props.theme === 'dark' ? '#333' : '#e0e0e0'};
        border-radius: 12px;
        padding: 1rem;
        box-shadow: 0 4px 20px rgba(0,0,0,0.05);
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 8px 30px rgba(0,0,0,0.1);
        }
    }

    textarea {
        width: 100%;
        min-height: 180px;
        border: none;
        background: transparent;
        color: ${props => props.theme === 'dark' ? '#fff' : '#333'};
        font-family: inherit;
        font-size: 0.95rem;
        line-height: 1.6;
        resize: none;
        outline: none;
        padding-top: 20px;
    }

    .copy-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        background: ${props => props.theme === 'dark' ? '#333' : '#eee'};
        border: none;
        border-radius: 6px;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: ${props => props.theme === 'dark' ? '#bbb' : '#666'};
        transition: all 0.2s;
        z-index: 5;

        &:hover {
            color: #EE7B1C;
            background: ${props => props.theme === 'dark' ? '#444' : '#ddd'};
        }
    }

    .action-buttons {
        display: flex;
        gap: 1rem;
        width: 100%;
        justify-content: center;
    }

    .linkedin-btn {
        border: none;
        background: #0077b5;
        padding: 0.7rem 1.8rem;
        color: #fff;
        border-radius: 30px;
        font-weight: 600;
        transition: all 0.3s ease;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.8rem;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(0, 119, 181, 0.3);
    
        &:hover {
            background: #006da2;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0, 119, 181, 0.4);
        }

        &:active {
            transform: translateY(0);
        }
    }
`;

const LoaderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    gap: 1rem;
    text-align: center;
    p {
        font-weight: 500;
        color: ${props => props.theme === 'dark' ? '#aaa' : '#666'};
    }
`;

const InsiderConnectHandler = ({ loading, data, job }) => {
    const { themeName } = useTheme();
    const [open, setOpen] = useState(false);
    const [selectedInsider, setSelectedInsider] = useState(null);
    const [curatedMessage, setCuratedMessage] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleOpenModal = async (insider) => {
        setSelectedInsider(insider);
        setOpen(true);
        setIsGenerating(true);
        setCuratedMessage("");
        setCopied(false);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/user/curate-insider-message', {
                insider,
                job
            }, {
                headers: { 'x-access-token': token }
            });
            if (response.status === 200) {
                setCuratedMessage(response.data.message);
            } else {
                setCuratedMessage("Unable to curate message at this time. Please try again or type your own.");
            }
        } catch (error) {
            console.error("Error curating message:", error);
            setCuratedMessage("An error occurred while curating the message.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(curatedMessage);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSendToLinkedin = () => {
        if (selectedInsider?.url) {
            window.open(selectedInsider.url, '_blank');
        } else if (selectedInsider?.link) {
            window.open(selectedInsider.link, '_blank');
        } else {
            const query = encodeURIComponent(`${selectedInsider?.title} at ${job?.company || ''}`);
            window.open(`https://www.linkedin.com/search/results/all/?keywords=${query}`, '_blank');
        }
    };

    return (
        <>
            <AccordionContentStyled theme={themeName}>
                <p>Discover valuable connections within the company who might provide insights and potential referrals, giving your job application an inside edge.</p>
                {loading ? (<LoadingData/>) :
                    data && data.map((item, index) => (
                        <div key={index} className="dataObject">
                            <span className="info">
                                <ImageAltStyled theme={themeName}>
                                    {item?.img ? (
                                        <img
                                            src={item.img}
                                            alt={item.title || "Contact Logo"}
                                        />
                                    ) : (
                                        <div className="nameTag">{item.title?.charAt(0).toUpperCase() || 'C'}</div>
                                    )}
                                </ImageAltStyled>
                            <div className="details">
                                <p>{item.title}</p>
                                <p>{item?.subtitle && (item.subtitle.length > 40 ? item.subtitle.slice(0, 40) + '...' : item.subtitle)}</p>
                            </div>  
                            </span>
                            <button className="connect" onClick={() => handleOpenModal(item)}>
                                Connect
                            </button>
                        </div>
                    ))
                }
            </AccordionContentStyled>

            <MainModal isOpen={open} onClose={() => setOpen(false)}>
                <DataContainer theme={themeName}>
                    {isGenerating ? (
                        <LoaderWrapper theme={themeName}>
                            <Vortex
                                visible={true}
                                height="100"
                                width="100"
                                ariaLabel="vortex-loading"
                                wrapperStyle={{}}
                                wrapperClass="vortex-wrapper"
                                colors={["#3E8F93", "#0d3d40", "#3E8F93", "#0d3d40", "#3E8F93", "#0d3d40"]}
                            />
                            <p>Analyzing your resume and job role to curate a perfect intro...</p>
                        </LoaderWrapper>
                    ) : (
                        <>
                            <h1>Ready to Connect</h1>
                            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#888', marginTop: '-10px' }}>
                                Personalized message for <strong>{selectedInsider?.title}</strong>
                            </p>
                            <div className="message-box">
                                <textarea
                                    value={curatedMessage}
                                    onChange={(e) => setCuratedMessage(e.target.value)}
                                    placeholder="Your personalized message will appear here..."
                                />
                                <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
                                    {copied ? <FaCheck style={{ color: '#3E8F93' }} /> : <FaRegCopy />}
                                </button>
                            </div>
                            <div className="action-buttons">
                                <button className="linkedin-btn" onClick={handleSendToLinkedin}>
                                    Open {selectedInsider?.title?.split(" ")[0] ? `${selectedInsider.title.split(" ")[0]}'s` : "on"} LinkedIn <FaLinkedin />
                                </button>
                            </div>
                        </>
                    )}
                </DataContainer>
            </MainModal>
        </>
    );
};

export default InsiderConnectHandler;